import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
  closestCenter,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  MdAdd,
  MdCreateNewFolder,
  MdEdit,
  MdDelete,
  MdDriveFileMove,
  MdFolder,
  MdFolderOpen,
  MdOutlineFolder,
  MdArrowBack,
} from "react-icons/md";
import { useAppLanguage } from "hooks/useAppLanguage";
import { useCourse } from "../../context/CourseContext";
import FolderTree, { type FolderNode } from "./components/FolderTree";
import ItemCard from "./components/ItemCard";
import FolderCard from "./components/FolderCard";
import NewItemDialog from "./NewItemDialog";
import MoveToDialog from "./MoveToDialog";
import Modal from "components/ui/Modal";
import ConfirmDialog from "components/ui/ConfirmDialog";
import PlyrVideoPlayer from "components/ui/PlyrVideoPlayer";
import ContextMenu, { type ContextMenuItem } from "components/ui/ContextMenu";
import EmptyState from "components/ui/EmptyState";
import { Skeleton } from "components/ui/Skeleton";
import {
  instructorCourseFoldersService,
  SYSTEM_ROOT_FOLDER_TITLE,
  type CourseFolder,
  type FolderReorderRow,
} from "services/instructorCourseFoldersService";
import {
  instructorFolderItemsService,
  type FolderItem,
  type FolderItemReorderRow,
} from "services/instructorFolderItemsService";
import { instructorQuizzesService } from "services/instructorQuizzesService";
import { toastError, toastSuccess } from "lib/toast";

function buildTree(folders: CourseFolder[]): FolderNode[] {
  const map = new Map<number, FolderNode>();
  folders.forEach((f) => map.set(f.id, { ...f, children: [] }));
  const roots: FolderNode[] = [];
  folders
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id)
    .forEach((f) => {
      const node = map.get(f.id)!;
      if (f.parent_id && map.has(f.parent_id)) {
        map.get(f.parent_id)!.children!.push(node);
      } else {
        roots.push(node);
      }
    });
  return roots;
}

function collectDescendantIds(node: FolderNode): number[] {
  const acc: number[] = [node.id];
  node.children?.forEach((c) => acc.push(...collectDescendantIds(c)));
  return acc;
}

function findNode(tree: FolderNode[], id: number): FolderNode | null {
  for (const n of tree) {
    if (n.id === id) return n;
    if (n.children) {
      const hit = findNode(n.children, id);
      if (hit) return hit;
    }
  }
  return null;
}

function uniqById<T extends { id: number }>(rows: T[]): T[] {
  const map = new Map<number, T>();
  rows.forEach((r) => map.set(r.id, r));
  return Array.from(map.values());
}

function SortableItem({
  id,
  itemId,
  children,
}: {
  id: string;
  itemId: number;
  children: (opts: {
    dragHandleProps: React.HTMLAttributes<HTMLButtonElement>;
    dragging: boolean;
  }) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
  } = useSortable({
    id,
    data: { kind: "item", itemId },
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const handle = { ...attributes, ...listeners } as React.HTMLAttributes<HTMLButtonElement>;
  const showTopLine = isOver && active && active.id !== id;
  return (
    <div ref={setNodeRef} style={style} className="relative">
      {showTopLine && (
        <div className="pointer-events-none absolute -top-1 left-0 right-0 z-10 h-0.5 rounded-full bg-brand-500" />
      )}
      {children({ dragHandleProps: handle, dragging: isDragging })}
    </div>
  );
}

function SortableFolderPanel({
  id,
  folderId,
  children,
}: {
  id: string;
  folderId: number;
  children: (opts: {
    dragHandleProps: React.HTMLAttributes<HTMLButtonElement>;
    dragging: boolean;
  }) => React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    active,
  } = useSortable({
    id,
    data: { kind: "folderpanel", folderId },
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const handle = { ...attributes, ...listeners } as React.HTMLAttributes<HTMLButtonElement>;
  const showTopLine = isOver && active && active.id !== id;
  return (
    <div ref={setNodeRef} style={style} className="relative">
      {showTopLine && (
        <div className="pointer-events-none absolute -top-1 left-0 right-0 z-10 h-0.5 rounded-full bg-brand-500" />
      )}
      {children({ dragHandleProps: handle, dragging: isDragging })}
    </div>
  );
}

function FolderInsideDropZone({
  folderId,
  children,
}: {
  folderId: number;
  children: (opts: { setNodeRef: (el: HTMLDivElement | null) => void; isOver: boolean }) => React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `folderpanel-inside-${folderId}`,
    data: { kind: "folder-inside", folderId },
  });
  return <>{children({ setNodeRef, isOver })}</>;
}

function FolderNestDropZone({
  folderId,
  children,
}: {
  folderId: number;
  children: (opts: { setNodeRef: (el: HTMLDivElement | null) => void; isOver: boolean }) => React.ReactNode;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: `folderpanel-nest-${folderId}`,
    data: { kind: "folder-nest", folderId },
  });
  return <>{children({ setNodeRef, isOver })}</>;
}

function parsePanelId(id: unknown):
  | { kind: "item"; itemId: number }
  | { kind: "folderpanel"; folderId: number }
  | { kind: "folder-inside"; folderId: number }
  | null {
  const s = typeof id === "string" ? id : String(id ?? "");
  if (s.startsWith("item-")) {
    const n = Number(s.slice("item-".length));
    return Number.isFinite(n) ? { kind: "item", itemId: n } : null;
  }
  if (s.startsWith("folderpanel-")) {
    const n = Number(s.slice("folderpanel-".length));
    return Number.isFinite(n) ? { kind: "folderpanel", folderId: n } : null;
  }
  if (s.startsWith("folderpanel-inside-")) {
    const n = Number(s.slice("folderpanel-inside-".length));
    return Number.isFinite(n) ? { kind: "folder-inside", folderId: n } : null;
  }
  return null;
}

export default function ContentPage() {
  const language = useAppLanguage();
  const isArabic = language === "ar";
  const navigate = useNavigate();
  const { course, setCounts } = useCourse();
  const params = useParams();
  const selectedId = params.folderId ? Number(params.folderId) : null;

  const selectFolder = React.useCallback(
    (id: number | null) => {
      if (id == null) navigate("../content");
      else navigate(`../content/folders/${id}`);
    },
    [navigate]
  );

  const [folders, setFolders] = React.useState<CourseFolder[]>([]);
  const [items, setItems] = React.useState<FolderItem[]>([]);
  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const [loadingTree, setLoadingTree] = React.useState(true);
  const [loadingItems, setLoadingItems] = React.useState(false);
  // Single unified list layout (folders + items), no sections.

  // Menus & dialogs
  const [folderMenu, setFolderMenu] = React.useState<{ folder: CourseFolder; anchor: HTMLElement } | null>(
    null
  );
  const [itemMenu, setItemMenu] = React.useState<{ item: FolderItem; anchor: HTMLElement } | null>(null);
  const [newItemOpen, setNewItemOpen] = React.useState(false);
  const [newFolderOpen, setNewFolderOpen] = React.useState(false);
  const [newFolderTitle, setNewFolderTitle] = React.useState("");
  const [newFolderParent, setNewFolderParent] = React.useState<number | null>(null);
  const [moveFolder, setMoveFolder] = React.useState<CourseFolder | null>(null);
  const [moveItem, setMoveItem] = React.useState<FolderItem | null>(null);
  const [confirmDeleteFolder, setConfirmDeleteFolder] = React.useState<CourseFolder | null>(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = React.useState<FolderItem | null>(null);
  const [previewItem, setPreviewItem] = React.useState<FolderItem | null>(null);

  const [ensuringRoot, setEnsuringRoot] = React.useState(false);

  const rootFolder = React.useMemo(
    () =>
      folders.find((f) => (f.parent_id ?? null) === null && f.title === SYSTEM_ROOT_FOLDER_TITLE) ?? null,
    [folders]
  );
  const visibleFolders = React.useMemo(
    () => folders.filter((f) => f.title !== SYSTEM_ROOT_FOLDER_TITLE),
    [folders]
  );

  const tree = React.useMemo(() => buildTree(visibleFolders), [visibleFolders]);
  const childFolders = React.useMemo(
    () =>
      selectedId == null || (rootFolder?.id != null && selectedId === rootFolder.id)
        ? visibleFolders
            .filter((f) => (f.parent_id ?? null) === null)
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id)
        : visibleFolders
            .filter((f) => (f.parent_id ?? null) === selectedId)
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id),
    [visibleFolders, selectedId, rootFolder?.id]
  );

  const mixedSortableIds = React.useMemo(() => {
    const folderRows = childFolders.map((f) => ({ id: `folderpanel-${f.id}`, sort: f.sort_order ?? 0 }));
    const itemRows = items.map((i) => ({ id: `item-${i.id}`, sort: i.sort_order ?? 0 }));
    return [...folderRows, ...itemRows]
      .sort((a, b) => a.sort - b.sort || String(a.id).localeCompare(String(b.id)))
      .map((r) => r.id);
  }, [childFolders, items]);

  const listParentId = React.useMemo(() => {
    if (selectedId == null) return null;
    if (rootFolder?.id != null && selectedId === rootFolder.id) return null;
    return selectedId;
  }, [selectedId, rootFolder?.id]);

  // Treat hidden __ROOT__ as the active folder when browsing "root".
  const activeFolderId = selectedId ?? rootFolder?.id ?? null;

  const resolveItemUrl = React.useCallback((item: FolderItem): string | null => {
    const anyItem = item as any;
    const u = (anyItem?.url ?? null) as string | null;
    if (u && typeof u === "string") return u;
    const p = (item.path ?? null) as string | null;
    if (p && /^https?:\/\//i.test(p)) return p;
    // Backend should provide `url` for R2 keys; fall back to nothing.
    return null;
  }, []);

  const reloadFolders = React.useCallback(async () => {
    setLoadingTree(true);
    try {
      const res = await instructorCourseFoldersService.list(course.id, {});
      const data = (res.data as CourseFolder[]) ?? [];
      setFolders(data);
      setCounts({ folders: data.filter((f) => f.title !== SYSTEM_ROOT_FOLDER_TITLE).length });
    } finally {
      setLoadingTree(false);
    }
  }, [course.id, setCounts]);

  const reloadItems = React.useCallback(
    async (folderId: number | null) => {
      if (folderId == null) {
        setItems([]);
        return;
      }
      setLoadingItems(true);
      try {
        const res = await instructorFolderItemsService.list(course.id, folderId);
        setItems((res.data as FolderItem[]) ?? []);
      } finally {
        setLoadingItems(false);
      }
    },
    [course.id]
  );

  React.useEffect(() => {
    reloadFolders();
  }, [reloadFolders]);

  // Ensure a hidden root folder exists so users can add items at the course "root" without selecting a folder.
  React.useEffect(() => {
    if (ensuringRoot) return;

    if (rootFolder) {
      if (selectedId == null) {
        // Auto-open root so "Add items" works immediately.
        selectFolder(rootFolder.id);
      }
      return;
    }

    let cancelled = false;
    setEnsuringRoot(true);
    void (async () => {
      try {
        await instructorCourseFoldersService.create(course.id, {
          title: SYSTEM_ROOT_FOLDER_TITLE,
          parent_id: null,
        }, { silent: true });
        if (cancelled) return;
        await reloadFolders();
      } catch {
        // ignore; UI will fall back to "select a folder"
      } finally {
        if (!cancelled) setEnsuringRoot(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id, rootFolder?.id, selectedId, ensuringRoot]);

  React.useEffect(() => {
    reloadItems(activeFolderId);
  }, [activeFolderId, reloadItems]);

  // Ensure child folders are available when a folder is selected (even if tree was loaded root-only).
  React.useEffect(() => {
    if (selectedId == null) return;
    const parent = folders.find((f) => f.id === selectedId);
    const expectsChildren = (parent?.children_count ?? 0) > 0;
    const alreadyHaveChildren = folders.some((f) => (f.parent_id ?? null) === selectedId);
    if (!expectsChildren || alreadyHaveChildren) return;

    let cancelled = false;
    void (async () => {
      try {
        const res = await instructorCourseFoldersService.list(course.id, { parent_id: selectedId });
        const data = (res.data as CourseFolder[]) ?? [];
        if (cancelled || !data.length) return;
        setFolders((cur) => uniqById([...cur, ...data]));
      } catch {
        // ignore; error handled by toast layer
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [course.id, folders, selectedId]);

  // Expand ancestors of selected
  React.useEffect(() => {
    if (selectedId == null) return;
    const byId = new Map(folders.map((f) => [f.id, f]));
    const toExpand: number[] = [];
    let cur = byId.get(selectedId);
    while (cur && cur.parent_id) {
      toExpand.push(cur.parent_id);
      cur = byId.get(cur.parent_id);
    }
    if (toExpand.length) {
      setExpanded((prev) => {
        const next = new Set(prev);
        toExpand.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [selectedId, folders]);

  const toggleExpand = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        // If the backend (or cache) only provided root folders, lazily fetch children on first expand.
        void (async () => {
          const alreadyHaveChildren = folders.some((f) => (f.parent_id ?? null) === id);
          const parent = folders.find((f) => f.id === id);
          const expectsChildren = (parent?.children_count ?? 0) > 0;
          if (expectsChildren && !alreadyHaveChildren) {
            try {
              const res = await instructorCourseFoldersService.list(course.id, { parent_id: id });
              const data = (res.data as CourseFolder[]) ?? [];
              if (data.length) {
                setFolders((cur) => {
                  const byId = new Map(cur.map((f) => [f.id, f]));
                  data.forEach((row) => byId.set(row.id, row));
                  return Array.from(byId.values());
                });
              }
            } catch {
              // ignore; errors will be shown by interceptor/toast layer
            }
          }
        })();
      }
      return next;
    });

  // -- Folder CRUD --
  const createFolder = async () => {
    if (!newFolderTitle.trim()) {
      toastError(isArabic ? "العنوان مطلوب" : "Title is required");
      return;
    }
    await instructorCourseFoldersService.create(course.id, {
      title: newFolderTitle.trim(),
      parent_id: newFolderParent,
      is_locked: true,
    });
    setNewFolderOpen(false);
    setNewFolderTitle("");
    setNewFolderParent(null);
    await reloadFolders();
  };

  const renameFolder = async (folder: CourseFolder, nextTitle: string) => {
    const prev = folders;
    setFolders((p) => p.map((f) => (f.id === folder.id ? { ...f, title: nextTitle } : f)));
    try {
      await instructorCourseFoldersService.update(course.id, folder.id, {
        title: nextTitle,
        parent_id: folder.parent_id ?? null,
        is_locked: folder.is_locked !== false,
      });
    } catch {
      setFolders(prev);
      throw new Error("rename failed");
    }
  };

  const toggleFolderAccess = async (folder: CourseFolder) => {
    const previous = folders;
    const nextLocked = folder.is_locked === false;
    setFolders((current) =>
      current.map((row) => (row.id === folder.id ? { ...row, is_locked: nextLocked } : row))
    );
    try {
      await instructorCourseFoldersService.update(course.id, folder.id, {
        title: folder.title,
        parent_id: folder.parent_id ?? null,
        is_locked: nextLocked,
      });
    } catch {
      setFolders(previous);
      toastError(isArabic ? "تعذر تحديث حالة المشاهدة" : "Could not update viewing access");
    }
  };

  const deleteFolder = async () => {
    if (!confirmDeleteFolder) return;
    await instructorCourseFoldersService.remove(course.id, confirmDeleteFolder.id);
    setConfirmDeleteFolder(null);
    if (selectedId === confirmDeleteFolder.id) selectFolder(null);
    await reloadFolders();
  };

  // -- Item CRUD --
  const renameItem = async (item: FolderItem, nextTitle: string) => {
    const prev = items;
    setItems((p) => p.map((i) => (i.id === item.id ? { ...i, title: nextTitle } : i)));
    try {
      if (!activeFolderId) throw new Error("no active folder");
      await instructorFolderItemsService.update(course.id, item.folder_id ?? activeFolderId, item.id, {
        type: item.type,
        title: nextTitle,
        is_locked: item.is_locked !== false,
        requires_previous: Boolean(item.requires_previous),
        path: item.path ?? null,
        meta: item.meta ?? null,
        quiz_id: item.quiz_id ?? null,
      });
    } catch {
      setItems(prev);
      throw new Error("rename failed");
    }
  };

  const toggleItemAccess = async (item: FolderItem) => {
    const previous = items;
    const nextLocked = item.is_locked === false;
    setItems((current) =>
      current.map((row) => (row.id === item.id ? { ...row, is_locked: nextLocked } : row))
    );
    try {
      const folderId = item.folder_id ?? activeFolderId;
      if (!folderId) throw new Error("no active folder");
      await instructorFolderItemsService.update(course.id, folderId, item.id, {
        type: item.type,
        title: item.title,
        is_locked: nextLocked,
        requires_previous: Boolean(item.requires_previous),
        path: item.path ?? null,
        meta: item.meta ?? null,
        quiz_id: item.quiz_id ?? null,
      });
    } catch {
      setItems(previous);
      toastError(isArabic ? "تعذر تحديث حالة المشاهدة" : "Could not update viewing access");
    }
  };

  const toggleItemRequiresPrevious = async (item: FolderItem) => {
    const previous = items;
    const nextRequiresPrevious = !Boolean(item.requires_previous);
    setItems((current) =>
      current.map((row) =>
        row.id === item.id ? { ...row, requires_previous: nextRequiresPrevious } : row
      )
    );
    try {
      const folderId = item.folder_id ?? activeFolderId;
      if (!folderId) throw new Error("no active folder");
      await instructorFolderItemsService.update(course.id, folderId, item.id, {
        type: item.type,
        title: item.title,
        is_locked: item.is_locked !== false,
        requires_previous: nextRequiresPrevious,
        path: item.path ?? null,
        meta: item.meta ?? null,
        quiz_id: item.quiz_id ?? null,
      });
    } catch {
      setItems(previous);
      toastError(
        isArabic ? "تعذر تحديث شرط المحتوى السابق" : "Could not update previous-content requirement"
      );
    }
  };

  const deleteItem = async () => {
    if (!confirmDeleteItem || !activeFolderId) return;
    await instructorFolderItemsService.remove(
      course.id,
      confirmDeleteItem.folder_id ?? activeFolderId,
      confirmDeleteItem.id
    );
    setConfirmDeleteItem(null);
    await reloadItems(activeFolderId);
  };

  // -- Move handlers --
  const moveFolderTo = async (folder: CourseFolder, parentId: number | null) => {
    try {
      await instructorCourseFoldersService.move(course.id, folder.id, { parent_id: parentId });
      toastSuccess(isArabic ? "تم نقل المجلد" : "Folder moved");
      await reloadFolders();
    } catch {
      // surface via interceptor
    }
  };

  const moveItemTo = async (item: FolderItem, folderId: number) => {
    if (!activeFolderId) return;
    try {
      await instructorFolderItemsService.move(
        course.id,
        item.folder_id ?? activeFolderId,
        item.id,
        { folder_id: folderId }
      );
      toastSuccess(isArabic ? "تم نقل العنصر" : "Item moved");
      await reloadItems(activeFolderId);
    } catch {
      // surface via interceptor
    }
  };

  // -- DnD --
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  type ActiveDrag =
    | { id: string; kind: "folder"; folder: CourseFolder }
    | { id: string; kind: "item"; item: FolderItem }
    | null;
  const [activeDrag, setActiveDrag] = React.useState<ActiveDrag>(null);

  // Prefer pointer-based detection so the thin between-strips win over the row
  // body when the pointer is on top of them. Fall back to rectIntersection so
  // far-off drops still resolve (keyboard DnD).
  const collisionDetection: CollisionDetection = React.useCallback((args) => {
    const activeKind =
      (args?.active?.data?.current as any)?.kind ?? parsePanelId(args?.active?.id as any)?.kind;

    // When dragging an item/folderpanel: if we're over a "folder-inside" dropzone, prefer it.
    if (activeKind === "item" || activeKind === "folderpanel") {
      const pointer = pointerWithin(args);
      if (pointer.length > 0) {
        const scored = pointer
          .map((c) => {
            const container = args.droppableContainers.find((d) => d.id === c.id);
            const k = (container?.data?.current as any)?.kind ?? null;
            // Prefer folder nesting dropzones when dragging a folder, and inside dropzones when dragging an item.
            const score =
              (activeKind === "folderpanel" && k === "folder-nest") ||
              (activeKind === "item" && k === "folder-inside")
                ? 0
                : 1;
            return { c, score };
          })
          .sort((a, b) => a.score - b.score);
        return scored.map((s) => s.c);
      }
      // Otherwise:
      // - for items: reorder against item rows only (avoid folders stealing `over`)
      // - for folders: allow picking the "inside" dropzone by distance too (more reliable nesting)
      if (activeKind === "item") {
        const itemOnly = args.droppableContainers.filter((d) => String(d.id).startsWith("item-"));
        return closestCenter({ ...(args as any), droppableContainers: itemOnly } as any);
      }
      const mixedWithInside = args.droppableContainers.filter((d) => {
        const id = String(d.id);
        return (
          id.startsWith("item-") ||
          id.startsWith("folderpanel-") ||
          id.startsWith("folderpanel-inside-") ||
          id.startsWith("folderpanel-nest-")
        );
      });
      return closestCenter({ ...(args as any), droppableContainers: mixedWithInside } as any);
    }

    // Default behavior (folders/tree): pointer first then rect intersection.
    const pointer = pointerWithin(args);
    if (pointer.length > 0) return pointer;
    return rectIntersection(args);
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const active = event.active;
    const data: any = active.data.current;
    const parsed = parsePanelId(active.id);
    if (data?.kind === "folder") {
      const folder = folders.find((f) => f.id === Number(data.folderId));
      if (folder) setActiveDrag({ id: String(active.id), kind: "folder", folder });
    } else if (data?.kind === "item" || parsed?.kind === "item") {
      const parsedItemId = parsed?.kind === "item" ? parsed.itemId : null;
      const itemId = Number(data?.itemId ?? parsedItemId);
      const item = items.find((i) => i.id === itemId);
      if (item) setActiveDrag({ id: String(active.id), kind: "item", item });
    } else if (parsed?.kind === "folderpanel") {
      const folder = folders.find((f) => f.id === Number(parsed.folderId));
      if (folder) setActiveDrag({ id: String(active.id), kind: "folder", folder });
    }
  };

  const reorderMixed = async (nextIds: string[]) => {
    if (!activeFolderId) return;
    const prevFolders = folders;
    const prevItems = items;

    const nextFolderSort = new Map<number, number>();
    const nextItemSort = new Map<number, number>();
    nextIds.forEach((id, idx) => {
      const pos = idx + 1;
      const parsed = parsePanelId(id);
      if (parsed?.kind === "folderpanel") nextFolderSort.set(parsed.folderId, pos);
      if (parsed?.kind === "item") nextItemSort.set(parsed.itemId, pos);
    });

    // Optimistic: update sort_order fields so mixedSortableIds re-renders in the new order.
    setFolders((cur) =>
      cur.map((f) => (nextFolderSort.has(f.id) ? { ...f, sort_order: nextFolderSort.get(f.id)! } : f))
    );
    setItems((cur) =>
      cur.map((it) => (nextItemSort.has(it.id) ? { ...it, sort_order: nextItemSort.get(it.id)! } : it))
    );

    try {
      const folderPayload: FolderReorderRow[] = childFolders
        .filter((f) => nextFolderSort.has(f.id))
        .map((f) => ({
          id: f.id,
          sort_order: nextFolderSort.get(f.id)!,
          parent_id: listParentId,
        }));

      const itemPayload: FolderItemReorderRow[] = items
        .filter((it) => nextItemSort.has(it.id))
        .map((it) => ({
          id: it.id,
          sort_order: nextItemSort.get(it.id)!,
          folder_id: it.folder_id ?? activeFolderId,
        }));

      await Promise.all([
        folderPayload.length ? instructorCourseFoldersService.reorder(course.id, folderPayload) : Promise.resolve(),
        itemPayload.length ? instructorFolderItemsService.reorder(course.id, itemPayload) : Promise.resolve(),
      ]);

      // Refresh to keep sidebar counts & server state in sync.
      await reloadFolders();
      await reloadItems(activeFolderId);
    } catch {
      setFolders(prevFolders);
      setItems(prevItems);
    }
  };

  const nestFolderInto = async (srcId: number, parentId: number) => {
    if (srcId === parentId) return;
    const src = folders.find((f) => f.id === srcId);
    if (!src) return;
    const srcNode = findNode(tree, srcId);
    if (srcNode) {
      const blocked = new Set(collectDescendantIds(srcNode));
      if (blocked.has(parentId)) {
        toastError(isArabic ? "لا يمكن نقل مجلد داخل نفسه" : "Cannot move a folder into itself");
        return;
      }
    }
    if ((src.parent_id ?? null) === parentId) return;
    try {
      await instructorCourseFoldersService.move(course.id, srcId, { parent_id: parentId });
      setExpanded((p) => {
        const n = new Set(p);
        n.add(parentId);
        return n;
      });
      toastSuccess(isArabic ? "تم نقل المجلد" : "Folder moved");
      await reloadFolders();
    } catch {
      // surfaced by interceptor
    }
  };

  const moveFolderToRoot = async (srcId: number) => {
    const src = folders.find((f) => f.id === srcId);
    if (!src || (src.parent_id ?? null) === null) return;
    try {
      await instructorCourseFoldersService.move(course.id, srcId, { parent_id: null });
      toastSuccess(isArabic ? "تم نقل المجلد" : "Folder moved");
      await reloadFolders();
    } catch {
      // surfaced by interceptor
    }
  };

  const placeFolderBetween = async (
    srcId: number,
    anchorId: number,
    position: "above" | "below",
    newParentId: number | null
  ) => {
    const src = folders.find((f) => f.id === srcId);
    if (!src) return;
    if (srcId === anchorId) return;

    // Block dropping into a descendant of src (only relevant when the new parent
    // is a descendant of src).
    const srcNode = findNode(tree, srcId);
    if (srcNode && newParentId != null) {
      const blocked = new Set(collectDescendantIds(srcNode));
      if (blocked.has(newParentId)) {
        toastError(isArabic ? "لا يمكن نقل مجلد داخل نفسه" : "Cannot move a folder into itself");
        return;
      }
    }

    const siblings = folders
      .filter((f) => (f.parent_id ?? null) === newParentId && f.id !== srcId)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
    const anchorIndex = siblings.findIndex((f) => f.id === anchorId);
    if (anchorIndex === -1) return;
    const insertIndex = position === "above" ? anchorIndex : anchorIndex + 1;

    const currentSiblingOrder = folders
      .filter((f) => (f.parent_id ?? null) === newParentId)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
    const currentIndex = currentSiblingOrder.findIndex((f) => f.id === srcId);
    // Bail if nothing changes
    if ((src.parent_id ?? null) === newParentId && currentIndex === insertIndex) return;

    const nextSiblings = [
      ...siblings.slice(0, insertIndex),
      { ...src, parent_id: newParentId },
      ...siblings.slice(insertIndex),
    ].map((f, i) => ({ ...f, sort_order: i + 1 }));

    // Optimistic update: replace siblings set in folders array
    const prevFolders = folders;
    const siblingIds = new Set(nextSiblings.map((f) => f.id));
    const updatedFolders = folders
      .filter((f) => !siblingIds.has(f.id))
      .concat(nextSiblings);
    setFolders(updatedFolders);

    try {
      if ((src.parent_id ?? null) !== newParentId) {
        await instructorCourseFoldersService.move(course.id, srcId, { parent_id: newParentId });
        if (newParentId != null) {
          setExpanded((p) => {
            const n = new Set(p);
            n.add(newParentId);
            return n;
          });
        }
      }
      const payload: FolderReorderRow[] = nextSiblings.map((f) => ({
        id: f.id,
        sort_order: f.sort_order!,
        parent_id: newParentId,
      }));
      await instructorCourseFoldersService.reorder(course.id, payload);
      if ((src.parent_id ?? null) !== newParentId) {
        toastSuccess(isArabic ? "تم نقل المجلد" : "Folder moved");
      }
      await reloadFolders();
    } catch {
      setFolders(prevFolders);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const activeData: any = active.data.current;
    const overData: any = over.data.current;
    const activeParsed = parsePanelId(active.id);
    const overParsed = parsePanelId(over.id);
    const oKind = overData?.kind ?? overParsed?.kind;

    // --- Guaranteed item behavior (by id), no reliance on data.current ---
    if (activeId.startsWith("item-")) {
      const aParsedId = activeParsed?.kind === "item" ? activeParsed.itemId : null;
      const aId = Number(activeData?.itemId ?? aParsedId);
      const item = items.find((i) => i.id === aId);
      if (!item) return;

      // Drop into folder (panel icon or tree): move
      if (overId.startsWith("folderpanel-inside-") || oKind === "folder-inside") {
        const oParsedFolderId = overParsed?.kind === "folder-inside" ? overParsed.folderId : null;
        const folderId = Number(overData?.folderId ?? oParsedFolderId);
        if (!Number.isFinite(folderId)) return;
        if (item.folder_id === folderId) return;
        await moveItemTo(item, folderId);
        return;
      }

      // Drop over another item: reorder
      if (overId.startsWith("item-") || overId.startsWith("folderpanel-")) {
        const oldIndex = mixedSortableIds.findIndex((x) => x === activeId);
        const newIndex = mixedSortableIds.findIndex((x) => x === overId);
        if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
        await reorderMixed(arrayMove(mixedSortableIds, oldIndex, newIndex));
        return;
      }
    }

    // Folder dragged in the mixed list dropped "inside" another folder -> nest
    if (activeId.startsWith("folderpanel-") && (overId.startsWith("folderpanel-inside-") || oKind === "folder-inside")) {
      const src = parsePanelId(activeId);
      const dest = parsePanelId(overId);
      const srcId = src?.kind === "folderpanel" ? src.folderId : null;
      const parentId = dest?.kind === "folder-inside" ? dest.folderId : null;
      if (!srcId || !parentId) return;
      await nestFolderInto(srcId, parentId);
      return;
    }

    // Folder dropped on a folder card (large nest zone) -> nest
    if (activeId.startsWith("folderpanel-") && (overId.startsWith("folderpanel-nest-") || oKind === "folder-nest")) {
      const src = parsePanelId(activeId);
      const parentId = Number(String(overId).replace("folderpanel-nest-", ""));
      const srcId = src?.kind === "folderpanel" ? src.folderId : null;
      if (!srcId || !Number.isFinite(parentId)) return;
      await nestFolderInto(srcId, parentId);
      return;
    }

    // Folderpanel reordered relative to items/folders in the mixed list
    if (activeId.startsWith("folderpanel-") && (overId.startsWith("item-") || overId.startsWith("folderpanel-"))) {
      const oldIndex = mixedSortableIds.findIndex((x) => x === activeId);
      const newIndex = mixedSortableIds.findIndex((x) => x === overId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      await reorderMixed(arrayMove(mixedSortableIds, oldIndex, newIndex));
      return;
    }

    // (mixed panel reorder is handled above via ids)

    // (legacy) tree drop is handled by the unified folder-inside branch above

    // Folder nested inside another folder
    if (activeData?.kind === "folder" && overData?.kind === "folder-inside") {
      await nestFolderInto(Number(activeData.folderId), Number(overData.folderId));
      return;
    }

    // Folder reordered between siblings (same or different parent)
    if (activeData?.kind === "folder" && overData?.kind === "folder-between") {
      await placeFolderBetween(
        Number(activeData.folderId),
        Number(overData.anchorId),
        overData.position as "above" | "below",
        overData.parentId == null ? null : Number(overData.parentId)
      );
      return;
    }

    // Folder dropped on the Root zone = move to top level (append)
    if (activeData?.kind === "folder" && overData?.kind === "folder-root") {
      await moveFolderToRoot(Number(activeData.folderId));
      return;
    }
  };

  // -- Context menu items --
  const folderMenuItems: ContextMenuItem[] = folderMenu
    ? [
        {
          id: "new-sub",
          label: isArabic ? "مجلد فرعي" : "New sub-folder",
          icon: <MdCreateNewFolder className="h-4 w-4" />,
          onSelect: () => {
            setNewFolderParent(folderMenu.folder.id);
            setNewFolderOpen(true);
            setFolderMenu(null);
          },
        },
        {
          id: "move",
          label: isArabic ? "نقل إلى..." : "Move to...",
          icon: <MdDriveFileMove className="h-4 w-4" />,
          onSelect: () => {
            setMoveFolder(folderMenu.folder);
            setFolderMenu(null);
          },
        },
        {
          id: "delete",
          label: isArabic ? "حذف" : "Delete",
          icon: <MdDelete className="h-4 w-4" />,
          danger: true,
          onSelect: () => {
            setConfirmDeleteFolder(folderMenu.folder);
            setFolderMenu(null);
          },
        },
      ]
    : [];

  const itemMenuItems: ContextMenuItem[] = itemMenu
    ? [
        {
          id: "move",
          label: isArabic ? "نقل إلى..." : "Move to...",
          icon: <MdDriveFileMove className="h-4 w-4" />,
          onSelect: () => {
            setMoveItem(itemMenu.item);
            setItemMenu(null);
          },
        },
        ...(itemMenu.item.type === "quiz" && itemMenu.item.quiz_id
          ? [
              {
                id: "open",
                label: isArabic ? "فتح الاختبار" : "Open quiz",
                icon: <MdEdit className="h-4 w-4" />,
                onSelect: () => {
                  navigate(`../quizzes/${itemMenu.item.quiz_id}`);
                  setItemMenu(null);
                },
              } as ContextMenuItem,
            ]
          : []),
        {
          id: "delete",
          label: isArabic ? "حذف" : "Delete",
          icon: <MdDelete className="h-4 w-4" />,
          danger: true,
          onSelect: () => {
            setConfirmDeleteItem(itemMenu.item);
            setItemMenu(null);
          },
        },
      ]
    : [];

  // Selected folder info
  const selectedFolder = selectedId ? folders.find((f) => f.id === selectedId) ?? null : null;
  const selectedParentId = selectedFolder?.parent_id ?? null;

  const disabledMoveIds = React.useMemo(() => {
    if (!moveFolder) return new Set<number>();
    const node = findNode(tree, moveFolder.id);
    if (!node) return new Set<number>();
    return new Set(collectDescendantIds(node));
  }, [moveFolder, tree]);

  const parentTitle = newFolderParent
    ? folders.find((f) => f.id === newFolderParent)?.title ?? null
    : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveDrag(null)}
    >
      <div className="grid grid-cols-12 gap-4">
        <aside className="col-span-12 md:col-span-4 xl:col-span-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-navy-800">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-800 dark:text-white">
                {isArabic ? "المجلدات" : "Folders"}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setNewFolderParent(null);
                  setNewFolderOpen(true);
                }}
                className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-600"
              >
                <MdCreateNewFolder className="h-4 w-4" />
                {isArabic ? "مجلد" : "Folder"}
              </button>
            </div>
            {loadingTree ? (
              <div className="flex flex-col gap-2 p-2">
                <Skeleton className="h-6 w-full" />
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-6 w-2/3" />
              </div>
            ) : tree.length === 0 ? (
              <EmptyState
                icon={<MdOutlineFolder className="h-5 w-5" />}
                title={isArabic ? "لا توجد مجلدات" : "No folders yet"}
                description={
                  isArabic
                    ? "ابدأ بإضافة مجلد لتنظيم محتوى الكورس."
                    : "Start by adding a folder to organize the course."
                }
                action={
                  <button
                    type="button"
                    onClick={() => setNewFolderOpen(true)}
                    className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600"
                  >
                    <MdCreateNewFolder className="h-4 w-4" />
                    {isArabic ? "إضافة مجلد" : "Add folder"}
                  </button>
                }
              />
            ) : (
              <FolderTree
                tree={tree}
                expanded={expanded}
                selectedId={selectedId}
                rtl={isArabic}
                activeDragId={activeDrag?.kind === "folder" ? activeDrag.id : null}
                onSelect={selectFolder}
                onToggle={toggleExpand}
                onRename={renameFolder}
                onOpenMenu={(folder, anchor) => setFolderMenu({ folder, anchor })}
                onAddChild={(parent) => {
                  setNewFolderParent(parent.id);
                  setExpanded((p) => {
                    const n = new Set(p);
                    n.add(parent.id);
                    return n;
                  });
                  setNewFolderOpen(true);
                }}
              />
            )}
          </div>
        </aside>

        <section className="col-span-12 min-w-0 md:col-span-8 xl:col-span-9">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-navy-800">
            <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {selectedId && (
                  <button
                    type="button"
                    onClick={() => selectFolder(selectedParentId)}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-900 dark:text-gray-200 dark:hover:bg-white/5"
                    aria-label={isArabic ? "رجوع" : "Back"}
                    title={isArabic ? "رجوع" : "Back"}
                  >
                    <MdArrowBack className={`h-4 w-4 ${isArabic ? "rotate-180" : ""}`} />
                    <span>{isArabic ? "رجوع" : "Back"}</span>
                  </button>
                )}
                <MdFolderOpen className="h-5 w-5 text-amber-500" />
                <h3 className="truncate text-sm font-bold text-gray-800 dark:text-white">
                  {selectedFolder
                    ? selectedFolder.title
                    : isArabic
                    ? "اختر مجلدًا لعرض العناصر"
                    : "Pick a folder to view items"}
                </h3>
                {selectedFolder && (
                  <span className="text-xs text-gray-500">#{selectedFolder.id}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={!activeFolderId}
                  onClick={() => setNewItemOpen(true)}
                  className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  <MdAdd className="h-5 w-5" />
                  {isArabic ? "إضافة عناصر" : "Add items"}
                </button>
              </div>
            </header>

            {!activeFolderId ? (
              <EmptyState
                icon={<MdOutlineFolder className="h-5 w-5" />}
                title={isArabic ? "لم يتم اختيار مجلد" : "No folder selected"}
                description={
                  isArabic
                    ? "اختر مجلدًا من اليسار لعرض العناصر أو أنشئ مجلدًا جديدًا."
                    : "Pick a folder from the left to browse its items, or create a new folder."
                }
              />
            ) : loadingItems ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : items.length === 0 && childFolders.length === 0 ? (
              <EmptyState
                icon={<MdAdd className="h-5 w-5" />}
                title={isArabic ? "هذا المجلد فارغ" : "This folder is empty"}
                description={
                  isArabic
                    ? "ارفع فيديوهات أو ملفات أو صور أو أنشئ اختبارًا."
                    : "Upload videos, files, images or create a quiz."
                }
                action={
                  <button
                    type="button"
                    onClick={() => setNewItemOpen(true)}
                    className="inline-flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600"
                  >
                    <MdAdd className="h-5 w-5" />
                    {isArabic ? "إضافة عناصر" : "Add items"}
                  </button>
                }
              />
            ) : (
              <SortableContext items={mixedSortableIds} strategy={verticalListSortingStrategy}>
                <ul className="flex flex-col gap-2">
                  {mixedSortableIds.map((rowId) => {
                    const parsed = parsePanelId(rowId);
                    if (parsed?.kind === "folderpanel") {
                      const f = childFolders.find((x) => x.id === parsed.folderId);
                      if (!f) return null;
                      return (
                        <li key={rowId}>
                          <FolderNestDropZone folderId={f.id}>
                            {({ setNodeRef: setNestRef, isOver: isNestOver }) => (
                              <div ref={setNestRef} className={isNestOver ? "ring-2 ring-brand-400/70 rounded-2xl" : ""}>
                                <SortableFolderPanel id={rowId} folderId={f.id}>
                                  {({ dragHandleProps, dragging }) => (
                                    <FolderInsideDropZone folderId={f.id}>
                                      {({ setNodeRef, isOver }) => (
                                        <FolderCard
                                          folder={f}
                                          onOpen={(folder) => selectFolder(folder.id)}
                                          onRename={renameFolder}
                                          onToggleAccess={toggleFolderAccess}
                                          onOpenMenu={(folder, anchor) => setFolderMenu({ folder, anchor })}
                                          dropRef={setNodeRef}
                                          dropActive={isOver}
                                          dragHandleProps={dragHandleProps}
                                          dragging={dragging}
                                        />
                                      )}
                                    </FolderInsideDropZone>
                                  )}
                                </SortableFolderPanel>
                              </div>
                            )}
                          </FolderNestDropZone>
                        </li>
                      );
                    }
                    if (parsed?.kind === "item") {
                      const it = items.find((x) => x.id === parsed.itemId);
                      if (!it) return null;
                      return (
                        <li key={rowId}>
                          <SortableItem id={rowId} itemId={it.id}>
                            {({ dragHandleProps, dragging }) => (
                              <ItemCard
                                item={it}
                                previewUrl={it.type === "image" ? resolveItemUrl(it) : null}
                                onRename={renameItem}
                                onToggleAccess={toggleItemAccess}
                                onToggleRequiresPrevious={toggleItemRequiresPrevious}
                                onOpenMenu={(item, anchor) => setItemMenu({ item, anchor })}
                                onOpen={(item) => {
                                  if (item.type === "quiz" && item.quiz_id) {
                                    instructorQuizzesService.invalidateQuiz(course.id, item.quiz_id);
                                    navigate(`../quizzes/${item.quiz_id}`);
                                    return;
                                  }
                                  setPreviewItem(item);
                                }}
                                dragHandleProps={dragHandleProps}
                                dragging={dragging}
                              />
                            )}
                          </SortableItem>
                        </li>
                      );
                    }
                    return null;
                  })}
                </ul>
              </SortableContext>
            )}
          </div>
        </section>
      </div>

      {/* Folder/Item context menus */}
      <ContextMenu
        open={!!folderMenu}
        anchor={folderMenu?.anchor ?? null}
        onClose={() => setFolderMenu(null)}
        items={folderMenuItems}
      />
      <ContextMenu
        open={!!itemMenu}
        anchor={itemMenu?.anchor ?? null}
        onClose={() => setItemMenu(null)}
        items={itemMenuItems}
      />

      {/* New folder modal */}
      <Modal
        open={newFolderOpen}
        onClose={() => {
          setNewFolderOpen(false);
          setNewFolderParent(null);
          setNewFolderTitle("");
        }}
        title={isArabic ? "مجلد جديد" : "New folder"}
        size="sm"
      >
        <div className="flex flex-col gap-3">
          <div className="inline-flex items-center gap-2 self-start rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-300">
            <MdFolder className="h-3.5 w-3.5 text-amber-500" />
            <span>
              {isArabic ? "يُنشأ داخل:" : "Inside:"}{" "}
              <span className="font-semibold text-gray-800 dark:text-white">
                {parentTitle ?? (isArabic ? "المستوى الأعلى" : "Top level")}
              </span>
            </span>
          </div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {isArabic ? "الاسم *" : "Title *"}
            <input
              autoFocus
              value={newFolderTitle}
              onChange={(e) => setNewFolderTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createFolder()}
              className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-400 dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setNewFolderOpen(false)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/10"
            >
              {isArabic ? "إلغاء" : "Cancel"}
            </button>
            <button
              type="button"
              onClick={createFolder}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
            >
              {isArabic ? "إنشاء" : "Create"}
            </button>
          </div>
        </div>
      </Modal>

      {/* New item */}
      {activeFolderId && (
        <NewItemDialog
          open={newItemOpen}
          onClose={() => setNewItemOpen(false)}
          onCreated={async () => {
            await reloadItems(activeFolderId);
            await reloadFolders(); // keep sidebar counts in sync (includes video)
          }}
          courseId={course.id}
          folderId={activeFolderId}
        />
      )}

      {/* Preview item */}
      <Modal
        open={!!previewItem}
        onClose={() => setPreviewItem(null)}
        title={
          isArabic
            ? `معاينة: ${previewItem?.title ?? ""}`
            : `Preview: ${previewItem?.title ?? ""}`
        }
        size="xl"
      >
        {previewItem ? (
          (() => {
            const url = resolveItemUrl(previewItem);
            if (!url) {
              return (
                <div className="text-sm text-gray-600 dark:text-gray-200">
                  {isArabic
                    ? "لا يوجد رابط معاينة متاح لهذا الملف."
                    : "No preview URL available for this item."}
                </div>
              );
            }
            if (previewItem.type === "video") {
              return (
                <PlyrVideoPlayer
                  key={url}
                  src={url}
                  brandName="Easyfollow"
                  brandInitial="E"
                />
              );
            }
            if (previewItem.type === "image") {
              return (
                <img
                  src={url}
                  alt={previewItem.title}
                  className="max-h-[70vh] w-full rounded-xl object-contain ring-1 ring-black/5 dark:ring-white/10"
                />
              );
            }
            return (
              <div className="flex flex-col gap-3">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex w-fit items-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                >
                  {isArabic ? "فتح الملف" : "Open file"}
                </a>
                <div className="text-xs text-gray-500">
                  {isArabic ? "سيتم فتح الملف في تبويب جديد." : "Opens in a new tab."}
                </div>
              </div>
            );
          })()
        ) : (
          <div />
        )}
      </Modal>

      {/* Move folder */}
      <MoveToDialog
        open={!!moveFolder}
        onClose={() => setMoveFolder(null)}
        tree={tree}
        title={isArabic ? "نقل المجلد" : "Move folder"}
        disabledIds={disabledMoveIds}
        onMove={async (target) => {
          if (moveFolder) await moveFolderTo(moveFolder, target.id);
        }}
      />

      {/* Move item */}
      <MoveToDialog
        open={!!moveItem}
        onClose={() => setMoveItem(null)}
        tree={tree}
        title={isArabic ? "نقل العنصر" : "Move item"}
        allowRoot
        onMove={async (target) => {
          if (!moveItem) return;
          const targetFolderId =
            target.id == null ? (rootFolder?.id ?? null) : Number(target.id);
          if (targetFolderId != null) await moveItemTo(moveItem, targetFolderId);
        }}
      />

      {/* Delete confirms */}
      <ConfirmDialog
        open={!!confirmDeleteFolder}
        onClose={() => setConfirmDeleteFolder(null)}
        onConfirm={deleteFolder}
        title={isArabic ? "حذف المجلد" : "Delete folder"}
        description={
          isArabic
            ? "سيتم حذف المجلد وكل محتوياته."
            : "The folder and all its contents will be deleted."
        }
      />
      <ConfirmDialog
        open={!!confirmDeleteItem}
        onClose={() => setConfirmDeleteItem(null)}
        onConfirm={deleteItem}
        title={isArabic ? "حذف العنصر" : "Delete item"}
        description={isArabic ? "هل تريد حذف هذا العنصر؟" : "Delete this item?"}
      />

      <DragOverlay dropAnimation={null}>
        {activeDrag?.kind === "folder" && (
          <div className="inline-flex items-center gap-2 rounded-lg border border-brand-400/40 bg-white px-3 py-1.5 text-sm font-semibold text-gray-800 shadow-xl ring-1 ring-black/5 dark:bg-navy-700 dark:text-white">
            <MdFolder className="h-4 w-4 text-amber-500" />
            <span className="max-w-xs truncate">{activeDrag.folder.title}</span>
          </div>
        )}
        {activeDrag?.kind === "item" && (
          <div className="max-w-sm shadow-xl">
            <ItemCard
              item={activeDrag.item}
              onRename={async () => {}}
              onToggleAccess={async () => {}}
              onToggleRequiresPrevious={async () => {}}
              onOpenMenu={() => {}}
              dragging
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
